/**
 * CetusEd NextGen — persisted refresh-token store.
 *
 * The ONE place the client touches localStorage for the refresh token. It lives
 * in core/storage/ on purpose: the "one gateway" rule (lint/one-gateway.ts,
 * root CLAUDE.md) centralizes all raw storage access here so nothing elsewhere
 * reaches localStorage/IndexedDB directly. core/session/ delegates to this.
 *
 * ┌─ SECURITY HONESTY (🪤) ────────────────────────────────────────────────────┐
 * │ This holds the REFRESH token as PLAINTEXT in localStorage, which is        │
 * │ readable by any XSS on this origin. It is NOT the PII envelope path (that  │
 * │ is gateway.ts, which encrypts and currently refuses) — the refresh token  │
 * │ is an opaque bearer credential, not a student PII field, so it does not    │
 * │ pass through field-level encryption. Persisting it in the clear is an      │
 * │ ACCEPTED staging tradeoff, stated not hidden. The hardening (server-set   │
 * │ HttpOnly;Secure;SameSite cookie; JS never touches the refresh token) is   │
 * │ ledgered as TASK-00083. Do not also persist the ACCESS token or the user  │
 * │ here — that widens the XSS blast radius for no gain.                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 */

/** localStorage key for the persisted refresh token. */
const REFRESH_TOKEN_KEY = '_CETUSED_REFRESH_TOKEN'

/** Read the persisted refresh token, or null (storage-disabled → null). */
export function readPersistedRefreshToken(): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch (err) {
    // Storage disabled (private mode / blocked). Treat as "no persisted token":
    // the in-memory session still works for this tab; it just will not survive a
    // reload. Handled, not swallowed.
    console.warn('[CetusEd] Could not read the persisted refresh token', err)
    return null
  }
}

/** Persist the refresh token (best effort — a storage failure is non-fatal). */
export function persistRefreshToken(token: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(REFRESH_TOKEN_KEY, token)
  } catch (err) {
    // Persistence failed; the live in-memory session is intact. Handled.
    console.warn('[CetusEd] Could not persist the refresh token', err)
  }
}

/** Forget the persisted refresh token (part of fail-closed session teardown). */
export function clearPersistedRefreshToken(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(REFRESH_TOKEN_KEY)
  } catch (err) {
    console.warn('[CetusEd] Could not clear the persisted refresh token', err)
  }
}
